/**
 * Choix et récupération de la source vidéo d'un evergreen, qui dispose depuis WELYA-490 d'une copie
 * de secours chez un second hébergeur. Logique pure : le DOM est piloté par `useEvergreenVideoSource`.
 */

export type EvergreenSourceKind = 'primary' | 'fallback';

export type EvergreenSourceCandidates = {
  primaryUrl: string | null;
  fallbackUrl: string | null;
};

/**
 * Abandon de la sonde de fallback. La source principale étant attachée dès le départ, la course ne
 * retarde jamais la lecture : ce délai ne borne que la bande passante dépensée à départager.
 */
export const EVERGREEN_PROBE_GIVE_UP_MS = 10_000;

/**
 * Absence de progression au-delà de laquelle on soupçonne une source morte. Volontairement long :
 * sur une connexion lente une seule requête de plage peut dépasser quarante secondes sans que rien
 * n'aille mal, et un soupçon n'est jamais une preuve (voir `resolveEvergreenRecovery`).
 */
export const EVERGREEN_STALL_THRESHOLD_S = 30;

/** Durée de lecture saine qui redonne droit à une bascule sur une session longue. */
export const EVERGREEN_HEALTHY_PLAYBACK_S = 30;

const normalize = (url: string | null | undefined): string | null => {
  const trimmed = url?.trim();
  return trimmed ? trimmed : null;
};

/**
 * Deux URL identiques ne valent qu'un seul candidat : sonder la même adresse deux fois ne
 * départagerait rien et doublerait les requêtes.
 */
export function resolveEvergreenCandidates(
  primaryUrl: string | null | undefined,
  fallbackUrl: string | null | undefined,
): EvergreenSourceCandidates {
  const primary = normalize(primaryUrl);
  const fallback = normalize(fallbackUrl);
  return {
    primaryUrl: primary,
    fallbackUrl: fallback !== null && fallback !== primary ? fallback : null,
  };
}

export const hasEvergreenFallback = (candidates: EvergreenSourceCandidates): boolean =>
  candidates.primaryUrl !== null && candidates.fallbackUrl !== null;

export const evergreenSourceUrl = (
  candidates: EvergreenSourceCandidates,
  kind: EvergreenSourceKind,
): string | null => (kind === 'primary' ? candidates.primaryUrl : candidates.fallbackUrl);

export const otherEvergreenSource = (kind: EvergreenSourceKind): EvergreenSourceKind =>
  kind === 'primary' ? 'fallback' : 'primary';

/**
 * Arbitrage du démarrage, sur l'instant où chaque source est devenue jouable et non sur son état
 * courant : deux sources prêtes au moment de l'arbitrage ne disent pas laquelle a répondu la
 * première. `null` tant que la course est ouverte, la source principale restant attachée pendant
 * ce temps, la course ne peut donc pas retarder la lecture.
 */
export function resolveEvergreenRaceWinner(params: {
  primaryReadyAt: number | null;
  fallbackReadyAt: number | null;
  deadlineReached: boolean;
}): EvergreenSourceKind | null {
  const { primaryReadyAt, fallbackReadyAt } = params;
  if (primaryReadyAt !== null && fallbackReadyAt !== null) {
    // À égalité stricte on garde la principale : elle est déjà attachée, changer ne gagnerait rien.
    return fallbackReadyAt < primaryReadyAt ? 'fallback' : 'primary';
  }
  if (primaryReadyAt !== null) return 'primary';
  if (fallbackReadyAt !== null) return 'fallback';
  if (params.deadlineReached) return 'primary';
  return null;
}

/**
 * Une source qui lâche en cours de route n'émet le plus souvent aucune erreur : elle cesse
 * simplement d'alimenter la vidéo. Seule l'absence de progression permet de la détecter.
 *
 * `bufferedAhead` distingue une pause délibérée, ou imposée par la politique d'autoplay, d'une
 * vidéo à l'arrêt faute de données : sans lui, un evergreen bloqué au chargement passerait au
 * travers, ce qui est précisément le blocage constaté en production.
 */
export function isEvergreenPlaybackStalled(params: {
  documentVisible: boolean;
  ended: boolean;
  paused: boolean;
  bufferedAhead: boolean;
  secondsSinceLastProgress: number;
  thresholdSeconds?: number;
}): boolean {
  const { documentVisible, ended, paused, bufferedAhead, secondsSinceLastProgress } = params;
  if (!documentVisible || ended) return false;
  if (paused && bufferedAhead) return false;
  return secondsSinceLastProgress >= (params.thresholdSeconds ?? EVERGREEN_STALL_THRESHOLD_S);
}

export type EvergreenRecoveryAction = 'wait' | 'reload' | 'switch' | 'give-up';

/**
 * Que faire d'une source qui pose problème, selon la solidité de l'indice.
 *
 * Une erreur du navigateur est une preuve : on relance, puis on bascule, puis on abandonne. La
 * bascule est libre tant que l'autre source n'a pas été essayée ; une fois les deux essayées elle
 * n'est rouverte que par une lecture saine, sinon deux sources en panne s'échangeraient la lecture
 * en boucle.
 *
 * Une absence de progression n'est qu'un soupçon : sur une connexion lente elle est indiscernable
 * d'une panne. On ne relance donc jamais, la relance jetant tout le téléchargement déjà acquis, et
 * on n'abandonne jamais. Tout au plus une bascule, et seulement si la vidéo n'a strictement rien
 * lu, cas où il n'y a rien à perdre.
 */
export function resolveEvergreenRecovery(params: {
  candidates: EvergreenSourceCandidates;
  activeKind: EvergreenSourceKind;
  reason: 'error' | 'stall';
  reloadsOnActiveSource: number;
  otherSourceAlreadyTried: boolean;
  activeSourcePlayedHealthy: boolean;
  hasPlayedAnything: boolean;
  maxReloadsPerSource?: number;
}): EvergreenRecoveryAction {
  const other = otherEvergreenSource(params.activeKind);
  const otherUrl = evergreenSourceUrl(params.candidates, other);

  if (params.reason === 'stall') {
    if (params.hasPlayedAnything) return 'wait';
    if (otherUrl === null || params.otherSourceAlreadyTried) return 'wait';
    return 'switch';
  }

  const maxReloads = params.maxReloadsPerSource ?? 1;
  if (params.reloadsOnActiveSource < maxReloads) return 'reload';

  if (otherUrl === null) return 'give-up';
  if (params.otherSourceAlreadyTried && !params.activeSourcePlayedHealthy) return 'give-up';
  return 'switch';
}
